import _styled6 from "styled-components";
import _styled5 from "styled-components";
import _styled4 from "styled-components";
import _styled3 from "styled-components";
import _styled2 from "styled-components";
import _styled from "styled-components";
import {useTranslation} from "../../../../__snowpack__/link/packages/old-i18n/dist/i18n.esm.js";
import React, {forwardRef, useCallback, useImperativeHandle, useMemo, useRef} from "react";
import {cmpClassName, execFn, getLineHeight, getRandomString, parseSize, remToPixel, transformSize} from "../../../common/generic-react.functions.js";
import Icon from "../../atoms/icon/icon.component.js";
import {useValidationInput} from "../../atoms/input/input.hooks.js";
import Label from "../../atoms/label/label.component.js";
import CropImage from "../crop-image/crop-image.component.js";
import {useImageUploader} from "./image-uploader.hooks.js";
import {ImageContainer, ImageContentContainer, ImageUploaderContainer, ImageUploaderMainContainer} from "./image-uploader.styles.js";
function MainImageUploader({
  label,
  required,
  name,
  disabled,
  text,
  size = "base",
  width,
  height,
  hasCrop = true,
  value,
  rules,
  onUpload,
  onComplete,
  onDelete
}, ref) {
  const {
    fileUpload,
    fileDrop,
    imgInfo: [imgInfo],
    imageName: [imageName],
    show: [show, setShow],
    resultImage: [resultImage, setResultImage],
    src: [src, setSrc],
    srcImage: [srcImage]
  } = useImageUploader({
    hasCrop,
    value,
    onUpload
  });
  const {
    t
  } = useTranslation();
  const inputRef = useRef(null);
  const {
    errorMessage: [errorMessage],
    validateOnChange: [validateOnChange, setValidateOnChange],
    isValidInput
  } = useValidationInput({
    inputRef,
    rules
  });
  useImperativeHandle(ref, () => {
    let val = resultImage || src || "";
    if (val) {
      val = "filename:" + imageName + ":" + val;
    }
    return {
      el: inputRef.current,
      valid: (e) => {
        const valid = isValidInput(e);
        setValidateOnChange(!valid);
        return valid;
      },
      setValidateOnChange: (v = false) => setValidateOnChange(v),
      value: val,
      setValue: () => void 0
    };
  }, [imageName, isValidInput, resultImage, setValidateOnChange, src]);
  const sizePixel = useMemo(() => +parseSize(size, true), [size]);
  const lineHeight = useMemo(() => +getLineHeight(size).replace("rem", ""), [size]);
  width = useMemo(() => width || `${sizePixel + remToPixel(7 * lineHeight)}px`, [lineHeight, sizePixel, width]);
  height = useMemo(() => height || `${sizePixel + remToPixel(3.5 * lineHeight)}px`, [height, lineHeight, sizePixel]);
  text = useMemo(() => text || t("imageUploader.content"), [t, text]);
  const preventDefault = useCallback((e) => {
    e.preventDefault();
  }, []);
  const onCancel = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    setShow(false);
    if (validateOnChange) {
      isValidInput(true);
    }
  }, [isValidInput, setShow, validateOnChange]);
  const onCompleteAction = useCallback((img) => {
    setShow(false);
    setResultImage(img, (image) => {
      execFn(onComplete, image);
    });
    if (validateOnChange) {
      isValidInput(true);
    }
  }, [isValidInput, onComplete, setResultImage, setShow, validateOnChange]);
  const deleteImage = useCallback(() => {
    onCancel();
    setResultImage("", () => {
      execFn(onDelete);
    });
    setSrc("", () => {
      execFn(onDelete);
    });
    if (validateOnChange) {
      isValidInput(true);
    }
  }, [isValidInput, onCancel, onDelete, setResultImage, setSrc, validateOnChange]);
  const clickImage = useCallback(() => {
    setShow(true);
  }, [setShow]);
  const inputId = useMemo(() => getRandomString(), []);
  const hasResult = useMemo(() => !!resultImage || !!src, [resultImage, src]);
  const ImageInfo = useMemo(() => hasResult && /* @__PURE__ */ React.createElement(_StyledDiv, {
    $_css: [{
      display: "flex",
      flexDirection: "row",
      justifyContent: "center"
    }, {
      overflowWrap: "anywhere"
    }]
  }, /* @__PURE__ */ React.createElement(_StyledLabel, {
    tag: "span",
    size,
    disabled: !!disabled
  }, imageName), /* @__PURE__ */ React.createElement(_StyledIcon, {
    name: "trash",
    onClick: deleteImage
  })), [deleteImage, disabled, hasResult, imageName, size]);
  return /* @__PURE__ */ React.createElement(ImageUploaderMainContainer, {
    className: cmpClassName("image-uploader"),
    role: "image-uploader",
    size,
    width,
    errorMessage
  }, label && /* @__PURE__ */ React.createElement(_StyledLabel2, {
    className: "image-uploader__external-label",
    bold: "medium",
    size,
    disabled: !!disabled,
    required
  }, label), /* @__PURE__ */ React.createElement(ImageContainer, {
    size,
    disabled: !!disabled,
    width,
    height
  }, /* @__PURE__ */ React.createElement(ImageContentContainer, {
    hasResult,
    src: resultImage || src,
    alt: imageName,
    width,
    height,
    onClick: () => !src ? clickImage() : void 0
  }), /* @__PURE__ */ React.createElement(ImageUploaderContainer, {
    hasResult
  }, /* @__PURE__ */ React.createElement(_StyledLabel3, {
    className: "image-uploader__internal-label",
    tag: "label",
    onDragOver: preventDefault,
    onDragEnter: preventDefault,
    onDragLeave: preventDefault,
    onDrop: fileDrop,
    htmlFor: inputId,
    size,
    disabled: !!disabled
  }, /* @__PURE__ */ React.createElement(Icon, {
    name: "image",
    size: transformSize(size, 2)
  }), text && /* @__PURE__ */ React.createElement(_StyledSpan, null, text), /* @__PURE__ */ React.createElement("input", {
    ref: inputRef,
    name,
    type: "file",
    id: inputId,
    required,
    disabled,
    "aria-disabled": disabled,
    onChange: fileUpload,
    accept: "image/*"
  })))), ImageInfo, errorMessage && /* @__PURE__ */ React.createElement("div", {
    className: "input__error"
  }, errorMessage), !src && /* @__PURE__ */ React.createElement(CropImage, {
    show,
    image: srcImage,
    file: imgInfo,
    onCancel,
    onComplete: onCompleteAction
  }));
}
const ImageUploader = /* @__PURE__ */ React.memo(/* @__PURE__ */ forwardRef(MainImageUploader));
ImageUploader.displayName = "ImageUploader";
export default ImageUploader;
var _StyledDiv = _styled("div").withConfig({
  displayName: "image-uploadercomponent___StyledDiv",
  componentId: "sc-iy4txk-0"
})(["", ""], (p) => p.$_css);
var _StyledLabel = _styled(Label).withConfig({
  displayName: "image-uploadercomponent___StyledLabel",
  componentId: "sc-iy4txk-1"
})({
  paddingLeft: "0px",
  paddingRight: "0px",
  marginRight: "1rem"
});
var _StyledIcon = _styled(Icon).withConfig({
  displayName: "image-uploadercomponent___StyledIcon",
  componentId: "sc-iy4txk-2"
})({
  flex: "0 1 auto"
});
var _StyledLabel2 = _styled(Label).withConfig({
  displayName: "image-uploadercomponent___StyledLabel2",
  componentId: "sc-iy4txk-3"
})({
  paddingLeft: "0px",
  paddingRight: "0px"
});
var _StyledLabel3 = _styled(Label).withConfig({
  displayName: "image-uploadercomponent___StyledLabel3",
  componentId: "sc-iy4txk-4"
})({
  display: "flex",
  flexDirection: "column",
  verticalAlign: "middle",
  alignItems: "center",
  justifyContent: "center",
  position: "absolute",
  top: "0px",
  right: "0px",
  bottom: "0px",
  left: "0px",
  width: "100%",
  height: "100%"
});
var _StyledSpan = _styled("span").withConfig({
  displayName: "image-uploadercomponent___StyledSpan",
  componentId: "sc-iy4txk-5"
})({
  textAlign: "center"
});
